/**
 * Centralized contact filter utilities
 * Used across Dialer, Contacts, and other components
 */

/**
 * Check if a contact is internal (PBX user)
 * @param {Object} contact - Contact object
 * @returns {boolean}
 */
export const isInternal = (contact) => {
  return contact.presence === true || contact.tags?.includes('internal');
};

/**
 * Check if a contact is a test contact (contains "TEST" in name)
 * @param {Object} contact - Contact object
 * @returns {boolean}
 */
export const isTestContact = (contact) => {
  return contact.name?.toUpperCase().includes('TEST');
};

/**
 * Check if a contact is a call queue or service number
 * These should typically be filtered out from user-facing contact lists
 * @param {Object} contact - Contact object
 * @returns {boolean}
 */
export const isCallQueue = (contact) => {
  const queueKeywords = [
    'after hours', 'on call', 'operator', 'reception', 
    'appointments', 'results', 'enquiries', 'sales',
    'support', 'billing', 'general', 'queue', 'ivr',
    'voicemail', 'fax', 'conference', 'main line',
    'helpdesk', 'service', 'auto attendant'
  ];
  const name = contact.name?.toLowerCase() || '';
  return queueKeywords.some(keyword => name.includes(keyword));
};

/**
 * Filter contacts to exclude test contacts
 * @param {Array} contacts - Array of contact objects
 * @returns {Array}
 */
export const filterOutTestContacts = (contacts) => {
  return contacts.filter(c => !isTestContact(c));
};

/**
 * Filter contacts to exclude call queues
 * @param {Array} contacts - Array of contact objects
 * @returns {Array}
 */
export const filterOutCallQueues = (contacts) => {
  return contacts.filter(c => !isCallQueue(c));
};

/**
 * Get display-ready contacts (excludes test contacts and call queues)
 * @param {Array} contacts - Array of contact objects
 * @returns {Array}
 */
export const getDisplayContacts = (contacts) => {
  return contacts.filter(c => !isTestContact(c) && !isCallQueue(c));
};

/**
 * Get internal contacts only
 * @param {Array} contacts - Array of contact objects
 * @param {boolean} excludeQueues - Whether to exclude call queues (default: true)
 * @returns {Array}
 */
export const getInternalContacts = (contacts, excludeQueues = true) => {
  let filtered = contacts.filter(c => isInternal(c) && !isTestContact(c));
  if (excludeQueues) {
    filtered = filtered.filter(c => !isCallQueue(c));
  }
  return filtered;
};

/**
 * Get external contacts only
 * @param {Array} contacts - Array of contact objects
 * @returns {Array}
 */
export const getExternalContacts = (contacts) => {
  return contacts.filter(c => !isInternal(c) && !isTestContact(c));
};

/**
 * Get contact counts by type
 * @param {Array} contacts - Array of contact objects
 * @param {boolean} excludeQueues - Whether to exclude call queues from internal count
 * @returns {Object} { total, internal, external }
 */
export const getContactCounts = (contacts, excludeQueues = true) => {
  const displayContacts = excludeQueues ? getDisplayContacts(contacts) : filterOutTestContacts(contacts);
  const internal = displayContacts.filter(c => isInternal(c)).length;
  const external = displayContacts.filter(c => !isInternal(c)).length;
  
  return {
    total: displayContacts.length,
    internal,
    external
  };
};
